/**
 * Phase 6e2c-3 — ACCESSORY SELECTOR pure data helpers (§33.8 / §51.2 / §51.4).
 *
 * Ported verbatim from accessory.selector.js (the View's non-DOM methods):
 *  - _getAvailableAirwayDimensions (aw label + values per plane family)
 *  - getSeriesOptions / getManufacturerOptions (cascade filters)
 *  - _updateMountingImage src resolution (louver series-merge map + fallbacks)
 *  - smartlist URL builder (loadSmartlist QS assembly)
 *  - the dataGetter "position" resolution: mounting_plane row + value attached.
 *
 * Everything here is a pure function over the staging model JSON + cmsAccessory.
 * No DOM, no fetch — the React selector calls these to assemble template data.
 */

export interface PlaneRow {
  id: number;
  value: string;
  name: string;
  plane?: string;
  [k: string]: unknown;
}

export interface MfgSeriesRow {
  id: number;
  series: Array<{ id: number; [k: string]: unknown }>;
  [k: string]: unknown;
}

export const parseParams = (raw: unknown): Record<string, unknown> => {
  if (raw == null || raw === '') return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
};

/** Legacy ifCompare '==' (loose, ids cross int/str). */
export const eq = (a: unknown, b: unknown): boolean =>
  a != null && b != null && String(a) === String(b);

/**
 * HOOD — the angle boxes a style shows, i.e. the angles the user is expected to
 * supply. NOT a legacy port: this is the answer to #2161's open question (what
 * should happen when a style's `CMSAHUHoodStyleDefaultSettings` row leaves an
 * `angle_<n>_default` empty, so switching to that style lands a BLANK angle on
 * the hood). Decision: refuse the selection — grey out #select and mark the
 * empty box — rather than save a hood whose 3-D model generation then fails in
 * the background with nothing shown to the user.
 *
 * The keys are `master_ahu_hood_style` ids, and the lists are exactly the boxes
 * `AccessorySelectorBody`'s hood branch renders (hood.selector.tmpl:103-132):
 * angle_1 for every style, angle_2/angle_3 for 3- and 4-sided, angle_4 for
 * 4-sided only. angle_5/angle_6 are derived server-side (`get_angle_5`) and
 * have no box, so they are not the user's to fill in.
 */
export const HOOD_STYLE_ANGLES: Record<string, string[]> = {
  1: ['angle_1'], // 2-Sided Straight
  2: ['angle_1'], // 2-Sided Curved
  3: ['angle_1', 'angle_2', 'angle_3'], // 3-Sided
  4: ['angle_1', 'angle_2', 'angle_3', 'angle_4'], // 4-Sided
};

/**
 * The angle boxes to render for a hood, in order. THE source for both the row
 * of inputs and the #select gate, so the two cannot drift apart and leave
 * #select greyed out by a box that is not on screen.
 *
 * A style this build does not know — and a hood that has no style yet, which
 * `save()` defaults — gets the angle_1 box alone, which is what the template
 * renders unconditionally.
 */
export function hoodShownAngles(model: Record<string, unknown>): string[] {
  const styleId = model.style_id;
  if (styleId == null) return ['angle_1'];
  return HOOD_STYLE_ANGLES[String(styleId)] ?? ['angle_1'];
}

/**
 * Which of those boxes are empty. Empty list = nothing to flag, nothing to
 * block. A hood with no style is never gated: its boxes are shown, but which
 * angles the style will want is not known yet.
 */
export function hoodMissingAngles(model: Record<string, unknown>): string[] {
  if (model.style_id == null) return [];
  return hoodShownAngles(model).filter(
    (angle) => model[angle] == null || model[angle] === ''
  );
}

/** mounting_plane rows EXCLUDING internal_wall_* (render :246-250). */
export function planesOnCabinet(cms: Record<string, unknown>): PlaneRow[] {
  const list = (cms.mounting_plane as PlaneRow[] | undefined) ?? [];
  return list.filter((el) => !/^internal_wall_[a-z]+/.test(String(el.value ?? '')));
}

/** Resolve the plane row for a given mounting_plane_id (dataGetter contract). */
export function resolvePlane(
  cms: Record<string, unknown>,
  mountingPlaneId: unknown
): PlaneRow | undefined {
  if (mountingPlaneId == null) return undefined;
  const list = (cms.mounting_plane as PlaneRow[] | undefined) ?? [];
  return list.find((p) => eq(p.id, mountingPlaneId));
}

/** _getMountingPlaneValue — the plane.value for the staged mounting_plane_id. */
export function mountingPlaneValue(
  cms: Record<string, unknown>,
  mountingPlaneId: unknown
): string | null {
  const plane = resolvePlane(cms, mountingPlaneId);
  return plane ? String(plane.value ?? '') : null;
}

export interface AwDimensions {
  show_available_airway: boolean;
  aw_label?: string;
  aw_first_value?: unknown;
  aw_second_value?: unknown;
}

/** _getAvailableAirwayDimensions (:371-402). */
export function getAvailableAirwayDimensions(
  planeValue: string | null,
  section: { aw_width?: unknown; aw_height?: unknown; aw_length?: unknown },
  mountingPlaneId: unknown
): AwDimensions {
  const id = mountingPlaneId == null ? null : Number(mountingPlaneId);
  const show = id == null || id <= 6;
  if (!show) return { show_available_airway: false };
  if (planeValue === 'floor' || planeValue === 'roof') {
    return {
      show_available_airway: true,
      aw_label: 'Available W x L',
      aw_first_value: section.aw_width,
      aw_second_value: section.aw_length,
    };
  }
  if (planeValue === 'wall_left' || planeValue === 'wall_right') {
    return {
      show_available_airway: true,
      aw_label: 'Available L x H',
      aw_first_value: section.aw_length,
      aw_second_value: section.aw_height,
    };
  }
  return {
    show_available_airway: true,
    aw_label: 'Available Airway W x H',
    aw_first_value: section.aw_width,
    aw_second_value: section.aw_height,
  };
}

/** getSeriesOptions (:404-428). */
export function getSeriesOptions(
  cms: Record<string, unknown>,
  type: string,
  mfgId: number | null,
  orientationId: number | null
): Array<{ id: number; [k: string]: unknown }> {
  const manufacturerSeries = (cms.manufacturer_series as MfgSeriesRow[] | undefined) ?? [];
  const allSeries = (cms.series as Array<{ id: number }> | undefined) ?? [];

  if (type === 'louver' && orientationId && !mfgId) {
    const bo = (cms.blade_orientation as Array<{ id: number; series?: Array<{ id: number }> }> | undefined) ?? [];
    const entry = bo.find((o) => eq(o.id, orientationId));
    if (entry && entry.series) return entry.series;
    return allSeries;
  }
  if (!mfgId || !manufacturerSeries.length) return allSeries;
  const mfgEntry = manufacturerSeries.find((m) => eq(m.id, mfgId));
  if (!mfgEntry) return allSeries;
  if (type === 'louver' && orientationId) {
    const filtered = mfgEntry.series.filter(
      (s) => (s.blade_orientation as { id?: number } | undefined)?.id === orientationId
    );
    return filtered.length ? filtered : mfgEntry.series;
  }
  return mfgEntry.series;
}

/** getManufacturerOptions (:430-448). */
export function getManufacturerOptions(
  cms: Record<string, unknown>,
  type: string,
  seriesId: number | null,
  orientationId: number | null
): Array<{ id: number; [k: string]: unknown }> {
  const manufacturerSeries = (cms.manufacturer_series as MfgSeriesRow[] | undefined) ?? [];
  const allManufacturers = (cms.manufacturer as Array<{ id: number }> | undefined) ?? [];
  if (seriesId && manufacturerSeries.length) {
    return manufacturerSeries.filter((mfg) => mfg.series.some((s) => eq(s.id, seriesId)));
  }
  if (type === 'louver' && orientationId) {
    const bo = (cms.blade_orientation as Array<{ id: number; mfgs?: Array<{ id: number }> }> | undefined) ?? [];
    const entry = bo.find((o) => eq(o.id, orientationId));
    if (entry && entry.mfgs) return entry.mfgs;
  }
  return allManufacturers;
}

/**
 * getFrameMtlOptions (`accessory.selector.js:556-577`) — LOUVER only.
 *
 * Frame material has no `manufacturer_series`-style cross-reference of its own,
 * so legacy derives the allowed set from `cms.combo_smartlist` (the distinct
 * mfg / series / blade / frame product lines) narrowed by whatever of
 * mfg_id / series_id / orientationId is already selected. Falls back to the
 * unfiltered `cms.frame_mtl` list when `combo_smartlist` is absent (damper) or
 * when the narrowing leaves nothing (`options.length ? options : allFrameMtl`,
 * :576 — a legacy oddity kept verbatim: an impossible combination re-opens the
 * FULL list rather than emptying the select).
 */
export function getFrameMtlOptions(
  cms: Record<string, unknown>,
  mfgId: number | null,
  seriesId: number | null,
  orientationId: number | null
): Array<{ id: number; name: string }> {
  type SlRow = {
    mfg_id?: unknown;
    series_id?: unknown;
    blade_orientation_id?: unknown;
    frame_mtl_id?: number | null;
    frame_mtl_name?: string;
  };
  const comboSmartlist = (cms.combo_smartlist as SlRow[] | undefined) ?? [];
  const allFrameMtl = (cms.frame_mtl as Array<{ id: number; name: string }> | undefined) ?? [];
  if (!comboSmartlist.length) return allFrameMtl;

  const filtered = comboSmartlist.filter((r) => {
    if (mfgId && !eq(r.mfg_id, mfgId)) return false;
    if (seriesId && !eq(r.series_id, seriesId)) return false;
    if (orientationId && !eq(r.blade_orientation_id, orientationId)) return false;
    return true;
  });

  const seen = new Set<number>();
  const options: Array<{ id: number; name: string }> = [];
  filtered.forEach((r) => {
    if (r.frame_mtl_id == null || seen.has(r.frame_mtl_id)) return;
    seen.add(r.frame_mtl_id);
    options.push({ id: r.frame_mtl_id, name: String(r.frame_mtl_name ?? '') });
  });
  return options.length ? options : allFrameMtl;
}

/**
 * ★ `_syncManufacturerSeriesFilters` (`accessory.selector.js:722-864`) — the
 * shared-identity cascade for damper + louver, ported as a pure function.
 *
 * ⚠ THE MECHANISM MATTERS, not just the resulting set. Legacy does NOT rebuild
 * the mfg / series / frame `<select>`s from a filtered list — the Handlebars
 * templates emit the FULL `cms.manufacturer` / `cms.series` / `cms.frame_mtl`
 * lists (`louver.selector.tmpl:54-56 / :66-68 / :78-80`, damper `:29 / :41 /
 * :155`) in the order the server served them, and this function only
 * `toggleClass('hide', …)`s the options outside the allowed set
 * (`:753-757`, `:792-796`, `:830-834`). Bootstrap's `.hide{display:none
 * !important}` (`vendor/bootstrap-3.3.4-dist/css/bootstrap.min.css`) is what
 * actually removes them, and the custom `.ka-series-select` widget filters on
 * the same class (`:2047`).
 *
 * That distinction is user-visible in the ORDER. Rendering the filtered list
 * directly (what this port did before) inherits the order of whichever
 * cross-reference array the filter came from — `blade_orientation[].mfgs` /
 * `[].series` / `manufacturer_series[].series` — which the API serves UNSORTED,
 * while `cms.manufacturer` / `cms.series` arrive sorted. Measured on dev3 with
 * `blade_orientation_id = 1` and no manufacturer picked:
 *   legacy `Greenheck, Modular Framing Systems, NAMM, Nailor Industries,
 *           Ruskin, TAMCO, Trolec, Vent Products Co., Ventex`   (server order)
 *   old React `Ruskin, TAMCO, Greenheck, Vent Products Co., …`  (`mfgs` order)
 * Same nine manufacturers, different order. Note the collation is the SERVER's
 * (`NAMM` before `Nailor` — uppercase-first, not `localeCompare`); reproducing
 * it means rendering the served array as-is, never re-sorting it client-side.
 *
 * Two more legacy rules this port was missing:
 *  · `:740-742` — when a manufacturer IS selected, the allowed manufacturer set
 *    is the FULL `cms.manufacturer` list, not the series cross-reference. So
 *    picking a manufacturer never strands the user on it.
 *  · `:825-861` — frame material is narrowed at all (louver only), and gets the
 *    sole-option auto-pick below.
 *
 * The auto-pick / invalidate-clear (`:766-787`, `:802-822`, `:840-860`) is
 * returned as `nextParams` rather than applied here: exactly one params object,
 * or `null` when nothing changed. All three allowed sets are computed from ONE
 * params snapshot (legacy reads `params` once at `:727-730` and computes
 * `allowedSeries` / `allowedManufacturers` at `:736-743`), so an auto-pick in
 * one block never feeds the next block's filter within the same pass.
 */
export interface ParamsCascade {
  /** option values (as strings) to render with `class="hide"`. */
  hiddenMfgIds: Set<string>;
  hiddenSeriesIds: Set<string>;
  hiddenFrameMtlIds: Set<string>;
  /** the auto-pick / invalidate-clear result — `null` when nothing changed. */
  nextParams: Record<string, unknown> | null;
}

const EMPTY_CASCADE: ParamsCascade = {
  hiddenMfgIds: new Set(),
  hiddenSeriesIds: new Set(),
  hiddenFrameMtlIds: new Set(),
  nextParams: null,
};

/** ids present in `rows` but not in `allowedIds` → the `hide` set. */
const hiddenFrom = (
  rows: Array<{ id: number; [k: string]: unknown }>,
  allowedIds: Set<number>
): Set<string> => {
  const out = new Set<string>();
  rows.forEach((r) => {
    if (!allowedIds.has(Number(r.id))) out.add(String(r.id));
  });
  return out;
};

export function syncManufacturerSeriesFilters(
  cms: Record<string, unknown>,
  type: string,
  params: Record<string, unknown>,
  comboMode: boolean
): ParamsCascade {
  // :724 — damper + louver only.
  if (type !== 'damper' && type !== 'louver') return EMPTY_CASCADE;

  const mfgId = (params.mfg_id as number | undefined) || null;
  const seriesId = (params.series_id as number | undefined) || null;
  const orientationId =
    (type === 'louver' && (params.blade_orientation_id as number | undefined)) || null;

  const manufacturerSeries = (cms.manufacturer_series as MfgSeriesRow[] | undefined) ?? [];
  const hasManufacturerSeries = manufacturerSeries.length > 0;
  // :734 — with no cross-reference AND no orientation there is nothing to
  // narrow by: legacy returns before touching a single option class.
  if (!hasManufacturerSeries && !orientationId) return EMPTY_CASCADE;

  const allSeries = (cms.series as Array<{ id: number; series?: unknown }> | undefined) ?? [];
  const allManufacturers =
    (cms.manufacturer as Array<{ id: number; name?: unknown }> | undefined) ?? [];
  const allFrameMtl = (cms.frame_mtl as Array<{ id: number; name?: unknown }> | undefined) ?? [];

  const allowedSeries = getSeriesOptions(cms, type, mfgId, orientationId);
  const allowedSeriesIds = new Set(allowedSeries.map((s) => Number(s.id)));

  // :740-742 — a SELECTED manufacturer opens the full list back up.
  const allowedManufacturers = mfgId
    ? allManufacturers
    : getManufacturerOptions(cms, type, seriesId, orientationId);
  const allowedMfgIds = new Set(allowedManufacturers.map((m) => Number(m.id)));

  const next = { ...params };
  let changed = false;

  // ---- series (:751-788) ------------------------------------------------
  const currentSeriesId = seriesId != null ? Number(seriesId) : null;
  const seriesStillActiveInCms =
    currentSeriesId != null && allSeries.some((s) => Number(s.id) === currentSeriesId);
  if (currentSeriesId && !allowedSeriesIds.has(currentSeriesId)) {
    // :767-770 — in combo mode a series that is merely filtered out is still a
    // legitimate clear; only a series GONE from CMS entirely is preserved (for
    // `_restoreComboLouverSelects`' disabled fallback option).
    if (!(comboMode && !seriesStillActiveInCms)) {
      delete next.series_id;
      delete next.series_name;
      changed = true;
    }
  } else if (!currentSeriesId && allowedSeriesIds.size === 1) {
    next.series_id = allowedSeries[0].id;
    next.series_name = (allowedSeries[0] as { series?: unknown }).series;
    changed = true;
  }

  // ---- manufacturer (:790-823) -----------------------------------------
  const currentMfgId = mfgId != null ? Number(mfgId) : null;
  const mfgStillActiveInCms =
    currentMfgId != null && allManufacturers.some((m) => Number(m.id) === currentMfgId);
  if (currentMfgId && !allowedMfgIds.has(currentMfgId)) {
    if (!(comboMode && !mfgStillActiveInCms)) {
      delete next.mfg_id;
      delete next.mfg_name;
      changed = true;
    }
  } else if (!currentMfgId && allowedMfgIds.size === 1) {
    next.mfg_id = allowedManufacturers[0].id;
    next.mfg_name = (allowedManufacturers[0] as { name?: unknown }).name;
    changed = true;
  }

  // ---- frame material (:825-861) — LOUVER only -------------------------
  let hiddenFrameMtlIds: Set<string> = new Set();
  if (type === 'louver') {
    const frameMtlId = (params.frame_mtl_id as number | undefined) || null;
    const allowedFrameMtl = getFrameMtlOptions(cms, mfgId, seriesId, orientationId);
    const allowedFrameMtlIds = new Set(allowedFrameMtl.map((f) => Number(f.id)));
    hiddenFrameMtlIds = hiddenFrom(allFrameMtl, allowedFrameMtlIds);
    const currentFrameMtlId = frameMtlId != null ? Number(frameMtlId) : null;
    const frameStillActiveInCms =
      currentFrameMtlId != null && allFrameMtl.some((f) => Number(f.id) === currentFrameMtlId);
    if (currentFrameMtlId && !allowedFrameMtlIds.has(currentFrameMtlId)) {
      if (!(comboMode && !frameStillActiveInCms)) {
        delete next.frame_mtl_id;
        delete next.frame_mtl_name;
        changed = true;
      }
    } else if (!currentFrameMtlId && allowedFrameMtlIds.size === 1) {
      // ★ THE sole-option auto-pick that sets `frame_mtl_id` the instant a
      // manufacturer is chosen (measured: mfg 47 → 4503 Aluminum-Extruded-Mill,
      // the only frame material across Greenheck's 7 combo_smartlist lines).
      next.frame_mtl_id = allowedFrameMtl[0].id;
      next.frame_mtl_name = allowedFrameMtl[0].name;
      changed = true;
    }
  }

  return {
    hiddenMfgIds: hiddenFrom(allManufacturers, allowedMfgIds),
    hiddenSeriesIds: hiddenFrom(allSeries, allowedSeriesIds),
    hiddenFrameMtlIds,
    nextParams: changed ? next : null,
  };
}

const LOUVER_IMG_BASE = '/static/css/common/images/louver/louver_';
const louverImgMerge: Record<string, string> = Object.fromEntries(
  (
    [
      [['ventex_2210', 'ventex_2215', 'ventex_2220', 'ventex_2225'], 'ventex_2210-2215-2220-2225'],
      [['ventex_2430', 'ventex_2435'], 'ventex_2430-2435'],
      [['ventex_acl600', 'ventex_acl605'], 'ventex_ac1600-1605'],
      [['trolec_hd445', 'trolec_td445'], 'trolec_td445-hd445'],
      [['trolec_hz245', 'trolec_tz245'], 'trolec_tz245-hz245'],
    ] as Array<[string[], string]>
  ).flatMap(([keys, val]) => keys.map((k) => [`${LOUVER_IMG_BASE}${k}.avif`, `${LOUVER_IMG_BASE}${val}.avif`]))
);

export interface MountingImageResult {
  /** primary src (may 404 → onError fallbacks numeric-series then default). */
  src: string;
  /** label "Mfg, Series" ('' when no mfg/series). */
  label: string;
  /** numeric-series fallback src (1st onError target). */
  numericFallback: string | null;
  /** default (final onError + no-mfg/series). */
  defaultSrc: string;
}

/** _updateMountingImage src resolution (:641-696). */
export function resolveMountingImage(
  type: string,
  cms: Record<string, unknown>,
  params: Record<string, unknown>
): MountingImageResult {
  const defaultSrc = `/static/css/common/images/${type}/${type}.avif`;
  const mfgId = params.mfg_id as number | undefined;
  const seriesId = params.series_id as number | undefined;
  if (!mfgId || !seriesId) {
    return { src: defaultSrc, label: '', numericFallback: null, defaultSrc };
  }
  const mfgList = (cms.manufacturer as Array<{ id: number; name: string }> | undefined) ?? [];
  const seriesList = (cms.series as Array<{ id: number; series: string }> | undefined) ?? [];
  const mfgEntry = mfgList.find((m) => eq(m.id, mfgId));
  const seriesEntry = seriesList.find((s) => eq(s.id, seriesId));
  if (!mfgEntry || !seriesEntry) {
    return { src: defaultSrc, label: '', numericFallback: null, defaultSrc };
  }
  const mfgName = mfgEntry.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const seriesName = String(seriesEntry.series).toLowerCase().replace(/[^a-z0-9]/g, '');
  const seriesNameNumbers = String(seriesEntry.series).replace(/[^0-9]/g, '');
  const basePath = `/static/css/common/images/${type}/${type}_${mfgName}`;
  const label = `${mfgEntry.name}, ${seriesEntry.series}`;
  const rawSrc = `${basePath}${
    type !== 'damper' || (mfgId != 135 && mfgId != 234) ? `_${seriesName}` : ''
  }.avif`;
  const src = louverImgMerge[rawSrc] || rawSrc;
  const numericFallback = seriesNameNumbers ? `${basePath}_${seriesNameNumbers}.avif` : null;
  return { src, label, numericFallback, defaultSrc };
}

/** loadSmartlist QS assembly (:1316-1333). Returns the three URLs. */
export function buildSmartlistUrls(args: {
  type: 'damper' | 'louver';
  mfgId: number;
  params: Record<string, unknown>;
  page: number;
  /** damper fan-intake / hood catalog base override (_catalogBase). */
  catalogBase?: string | null;
  /** damper extra constrained params (_catalogExtraParams). */
  catalogExtraParams?: string | null;
}): { itemsUrl: string; countUrl: string; totalUrl: string; base: string; combinedQs: string } {
  const { type, mfgId, params, page, catalogBase, catalogExtraParams } = args;
  const p = { ...params };
  delete (p as { spn?: unknown }).spn;
  const qs = Object.entries(p)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  const base =
    (type === 'damper' && catalogBase) ||
    `/api/v1.0/cms/${mfgId}/v1.0.0/ahu/${type}s/all/`;
  const extraQs = (type === 'damper' && catalogExtraParams) || '';
  const combinedQs = [qs, extraQs].filter(Boolean).join('&');
  const itemsUrl = `${base}?${combinedQs ? `${combinedQs}&` : ''}page=${page}`;
  const countUrl = `${base}count/${combinedQs ? `?${combinedQs}` : ''}`;
  const totalUrl = `${base}count/`;
  return { itemsUrl, countUrl, totalUrl, base, combinedQs };
}

/** _updateDamperCMS URL resolution (:890-957) — fan-intake / hood / static.
 * Returns the params-doc URL + catalog base/extra, or null for static cms. */
export function resolveDamperCmsUrl(args: {
  mfgId: number;
  hoodId: unknown;
  mountingPlaneValue: string | null;
  model: Record<string, unknown>;
}): { cmsUrl: string | null; catalogBase: string | null; catalogExtraParams: string | null } {
  const { mfgId, hoodId, mountingPlaneValue, model } = args;
  if (mountingPlaneValue === 'fan_intake') {
    const { min_height, max_height, min_width, max_width } = model as Record<string, number | undefined>;
    const cmsUrl = min_height
      ? `/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/params/?max_height=${max_height}&min_height=${min_height}&min_width=${min_width}&max_width=${max_width}`
      : '/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/params/';
    return {
      cmsUrl,
      catalogBase: `/api/v1.0/cms/${mfgId}/v1.0.0/ahu/fans/dampers/all/`,
      catalogExtraParams: min_height
        ? `max_height=${max_height}&min_height=${min_height}&min_width=${min_width}&max_width=${max_width}`
        : null,
    };
  }
  if (hoodId) {
    const { max_height, max_width } = model as Record<string, number | undefined>;
    if (!max_height || !max_width) {
      return { cmsUrl: null, catalogBase: null, catalogExtraParams: null };
    }
    return {
      cmsUrl: `/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/?max_height=${max_height}&max_width=${max_width}&hood=true`,
      catalogBase: null,
      catalogExtraParams: `max_height=${max_height}&max_width=${max_width}&hood=true`,
    };
  }
  // static (plain damper CMS).
  return { cmsUrl: 'static', catalogBase: null, catalogExtraParams: null };
}
